import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/PasswordInput';
import { Label } from '../components/ui/label';
import { AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { validateEmail, validateRequired } from '../utils/validations';
import { ErrorMessage, getInputClass } from '../components/ErrorMessage';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
    setFieldErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleEmailBlur = () => {
    const result = validateEmail(formData.email);
    if (!result.valid) setFieldErrors(prev => ({ ...prev, email: result.error }));
  };

  const handlePasswordBlur = () => {
    const result = validateRequired(formData.password, 'Password');
    if (!result.valid) setFieldErrors(prev => ({ ...prev, password: result.error }));
  };

  const validateForm = () => {
    const errors = {};
    const emailResult = validateEmail(formData.email);
    if (!emailResult.valid) errors.email = emailResult.error;
    const passwordResult = validateRequired(formData.password, 'Password');
    if (!passwordResult.valid) errors.password = passwordResult.error;
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validateForm()) return;
    setLoading(true);
    try {
      const userData = await login(formData.email, formData.password);
      toast.success('Login successful!');
      if (userData.role === 'vendor') {
        navigate('/vendor/dashboard');
      } else {
        navigate('/admin/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f8fafc]">
      {/* Tricolor Strip */}
      <div className="tricolor-strip" />

      {/* Minimal Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/iith-logo.png" alt="IIT Hyderabad" className="h-14 w-auto" />
          </Link>
          <Link to="/register">
            <Button variant="outline" className="text-[#003380] border-[#003380] hover:bg-[#003380]/5 hover:text-[#003380] text-sm font-medium" data-testid="header-register-btn">
              New Registration
            </Button>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Left Panel - Clean Gradient */}
        <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#001a44] via-[#003380] to-[#0052cc]" />
          {/* Subtle Pattern Overlay */}
          <div className="absolute inset-0 opacity-[0.04]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
          <div className="relative z-10 flex flex-col justify-center px-16 py-12">
            <div className="max-w-lg">
              <h1 className="text-4xl font-bold text-white font-serif leading-tight mb-4">
                Vendor Registration
                <br />
                <span className="text-blue-200 text-3xl">& Management Portal</span>
              </h1>
              <p className="text-blue-200/80 text-base leading-relaxed mb-10 max-w-md">
                Streamlined procurement process for vendor empanelment, 
                document verification, and approval management.
              </p>
              <div className="space-y-4">
                {[
                  'Digital vendor registration & document upload',
                  'Multi-level scrutiny & approval workflow',
                  'Real-time application status tracking'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-300 flex-shrink-0" />
                    <span className="text-blue-100/90 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Login Form */}
        <div className="flex-1 flex items-center justify-center px-6 py-12 lg:px-16">
          <div className="w-full max-w-[400px]">
            {/* Mobile logo */}
            <div className="lg:hidden text-center mb-8">
              <img src="/iith-logo.png" alt="IIT Hyderabad" className="h-16 w-auto mx-auto mb-3" />
              <h2 className="text-lg font-semibold text-slate-800">Vendor Registration Portal</h2>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Welcome back</h2>
              <p className="text-slate-500 mt-1 text-sm">Sign in to your vendor account</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="flex items-center gap-2.5 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="login-error-msg">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-slate-700 text-sm font-medium">Email Address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleEmailBlur}
                  placeholder="you@example.com"
                  className={`h-11 bg-white border-slate-300 focus:border-[#003380] focus:ring-[#003380]/20 ${getInputClass(fieldErrors.email)}`}
                  required
                  data-testid="login-email-input"
                />
                <ErrorMessage error={fieldErrors.email} />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-slate-700 text-sm font-medium">Password</Label>
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-xs text-[#003380] hover:text-[#002060] font-medium"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                </div>
                <PasswordInput
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handlePasswordBlur}
                  placeholder="Enter your password"
                  className={`h-11 bg-white border-slate-300 focus:border-[#003380] focus:ring-[#003380]/20 ${getInputClass(fieldErrors.password)}`}
                  required
                  data-testid="login-password-input"
                />
                <ErrorMessage error={fieldErrors.password} />
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-[#003380] hover:bg-[#002060] text-white font-medium text-sm shadow-sm"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            <p className="text-center text-sm text-slate-500 mt-6">
              Don&apos;t have an account?{' '}
              <Link to="/register" className="text-[#003380] hover:text-[#002060] font-medium" data-testid="register-link">
                Register here
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#003380] text-white py-5">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap justify-between items-center">
          <img src="/iith-logo.png" alt="IIT Hyderabad" className="h-10 w-auto brightness-0 invert" />
          <span className="text-xs text-blue-200">
            © {new Date().getFullYear()} Indian Institute of Technology Hyderabad. All rights reserved.
          </span>
        </div>
      </footer>
    </div>
  );
}
